import { Link } from "react-router-dom";
import { ArrowUpRight, Mail, MessageSquare, Send, RotateCcw, CalendarClock, Receipt as ReceiptIcon, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import StatusChip from "@/components/StatusChip";
import { fmtDate, fmtMoney, daysUntilDue } from "@/lib/bill-utils";
import { composeBillEmail, composePaymentReceiptEmail } from "@/lib/email-compose";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const BillCard = ({ bill, onEmailed }) => {
  const dleft = daysUntilDue(bill.due_date);
  const dueLabel =
    dleft === null
      ? ""
      : dleft < 0
        ? `${Math.abs(dleft)}d overdue`
        : dleft === 0
          ? "Due today"
          : `${dleft}d left`;

  const handleEmail = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const { attachmentName, markPromise } = composeBillEmail(bill);
    toast.success(
      attachmentName
        ? `Opening email client. Image "${attachmentName}" downloaded — attach it before sending.`
        : "Opening your email client. Marked as emailed.",
    );
    if (markPromise) await markPromise;
    if (onEmailed) onEmailed();
  };

  const lastPayment = bill.payments && bill.payments.length > 0 ? bill.payments[bill.payments.length - 1] : null;

  const handleEmailReceipt = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!lastPayment) return;
    if (!bill.email) {
      toast.error("No vendor email saved on this bill. Open it to add one.");
    }
    const { attachmentName, recipient } = composePaymentReceiptEmail(bill, lastPayment);
    toast.success(
      attachmentName
        ? `Opening email to ${recipient || "vendor"}. Receipt "${attachmentName}" downloaded — attach it before sending.`
        : `Opening email to ${recipient || "vendor"}.`,
    );
  };

  const handleCopyPayLink = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const origin =
      BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    const url = `${origin}/pay/${bill.id}`;
    const due = bill.due_date ? new Date(bill.due_date).toLocaleDateString("en-AU") : "";
    const owed = fmtMoney(bill.remaining_balance);
    const msg = [
      `Hi — your invoice ${bill.invoice_number} for ${owed} is`,
      due ? `due ${due}.` : "ready.",
      `Tap to mark it paid: ${url}`,
      "— The Outdoor Blind Company",
    ]
      .filter(Boolean)
      .join(" ");
    try {
      await navigator.clipboard.writeText(msg);
      toast.success("Pay link + SMS message copied. Paste into a text.");
    } catch {
      toast.error("Couldn't copy. Long-press to copy manually: " + url);
    }
  };

  return (
    <Link
      to={`/bills/${bill.id}`}
      data-testid={`bill-card-${bill.id}`}
      className="swiss-card block group relative"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusChip status={bill.status} />
            {bill.series_type === "recurring" && (
              <span className="chip bg-purple-50 text-purple-800 border-purple-200">
                <RotateCcw className="h-3 w-3" /> {bill.series_index}/{bill.series_total}
              </span>
            )}
            {bill.series_type === "plan" && (
              <span className="chip bg-sky-50 text-sky-800 border-sky-200">
                <CalendarClock className="h-3 w-3" /> Plan {bill.series_index}/{bill.series_total}
              </span>
            )}
            {bill.emailed && (
              <span
                className="chip bg-blue-50 text-blue-800 border-blue-200"
                data-testid={`chip-emailed-${bill.id}`}
              >
                <Mail className="h-3 w-3" /> Emailed
              </span>
            )}
            {bill.texted && (
              <span
                className="chip bg-indigo-50 text-indigo-800 border-indigo-200"
                data-testid={`chip-texted-${bill.id}`}
              >
                <MessageSquare className="h-3 w-3" /> Texted
              </span>
            )}
          </div>
          <div className="mt-4 font-display font-extrabold text-xl leading-tight truncate">
            {bill.company || "Untitled Vendor"}
          </div>
          <div className="mt-1 text-xs uppercase tracking-[0.2em] text-neutral-500 hash truncate">
            # {bill.invoice_number}
          </div>
        </div>
        <ArrowUpRight
          className="h-5 w-5 text-neutral-400 group-hover:text-black transition-colors flex-shrink-0"
          strokeWidth={2.5}
        />
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3 border-t border-neutral-200 pt-4">
        <div className="min-w-0">
          <div className="swiss-label">Due</div>
          <div className="font-semibold mt-1 text-sm whitespace-nowrap">
            {fmtDate(bill.due_date)}
          </div>
          {dueLabel && (
            <div
              className={`text-[10px] uppercase tracking-[0.2em] mt-0.5 whitespace-nowrap ${dleft < 0 ? "text-red-600" : dleft <= 3 ? "text-amber-700" : "text-neutral-500"}`}
            >
              {dueLabel}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="swiss-label">Owed</div>
          <div className="font-display font-extrabold mt-1 text-sm mono tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
            {fmtMoney(bill.remaining_balance)}
          </div>
          {bill.total_paid > 0 && (
            <div className="text-[10px] uppercase tracking-[0.2em] mt-0.5 text-neutral-500 whitespace-nowrap">
              Paid {fmtMoney(bill.total_paid)}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <div className="swiss-label">Total</div>
          <div className="font-semibold mt-1 text-sm mono tabular-nums whitespace-nowrap overflow-hidden text-ellipsis">
            {fmtMoney(bill.amount_due)}
          </div>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-neutral-100 flex flex-wrap justify-end gap-4">
        <button
          type="button"
          data-testid={`card-copy-paylink-${bill.id}`}
          onClick={handleCopyPayLink}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-600 hover:text-black transition-colors"
        >
          <LinkIcon className="h-3.5 w-3.5" /> Copy Pay Link
        </button>
        {lastPayment && (
          <button
            type="button"
            data-testid={`card-email-receipt-btn-${bill.id}`}
            onClick={handleEmailReceipt}
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-600 hover:text-black transition-colors"
          >
            <ReceiptIcon className="h-3.5 w-3.5" /> Email Receipt
          </button>
        )}
        <button
          type="button"
          data-testid={`card-email-btn-${bill.id}`}
          onClick={handleEmail}
          className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold text-neutral-600 hover:text-black transition-colors"
        >
          <Send className="h-3.5 w-3.5" /> Email Bill
        </button>
      </div>
    </Link>
  );
};

export default BillCard;
